import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    Tooltip,
    IconButton,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { MaterialReactTable } from "material-react-table";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import { useMaterialUIController } from "../../context";

import SpeedIcon from "@mui/icons-material/Speed";              // stability
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt"; // signal
import GraphicEqIcon from "@mui/icons-material/GraphicEq";      // noise / ber
import RestartAltIcon from "@mui/icons-material/RestartAlt";    // restarts
import BoltIcon from "@mui/icons-material/Bolt";                // voltage deviation
import LinkIcon from "@mui/icons-material/Link";
import Icon from "@mui/material/Icon";

function safeJsonParse(v) {
    if (v == null) return null;
    if (typeof v === "object") return v;
    if (typeof v !== "string") return null;
    try { return JSON.parse(v); } catch { return null; }
}

// 0 => red, 50 => yellow, 100 => green
function metricColor(value) {
    const v = Math.max(0, Math.min(100, Number(value) || 0));
    const hue = (v / 100) * 120;
    return `hsl(${hue}, 90%, 45%)`;
}

function HealthIcons({ health, connected }) {
    const h = safeJsonParse(health) || {};

    const connections = Number(h.connections);
    const signal = Number(h.signal);
    const ber = Number(h.ber);
    const restarts = Number(h.restarts);
    const voltageDeviation = Number(h.voltageDeviation);

    // helper to hide NaN
    const ok = (n) => Number.isFinite(n);

    const items = [
        ok(connections) && {
            key: "connections",
            icon: <SpeedIcon fontSize="small" />,
            value: connections,
            title: `Connection stability ${Math.round(connections)} %`,
        },
        ok(ber) && {
            key: "ber",
            icon: <GraphicEqIcon fontSize="small" />,
            value: ber,
            title: `Cellular signal noise ${Math.round(ber)} %`,
        },
        ok(signal) && {
            key: "signal",
            icon: <SignalCellularAltIcon fontSize="small" />,
            value: signal,
            title: `Cellular signal quality ${Math.round(signal)} %`,
        },

        // Optional extras (comment out if you only want 3 icons)
        ok(restarts) && {
            key: "restarts",
            icon: <RestartAltIcon fontSize="small" />,
            value: restarts,
            title: `Restarts ${Math.round(restarts)} %`,
        },
        ok(voltageDeviation) && {
            key: "voltageDeviation",
            icon: <BoltIcon fontSize="small" />,
            value: voltageDeviation,
            title: `Voltage deviation ${Math.round(voltageDeviation)} %`,
        },
    ].filter(Boolean);

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={connected ? "Connected" : "Disconnected"}>
        <span>
          <LinkIcon
              fontSize="small"
              style={{ color: connected ? metricColor(100) : metricColor(0) }}
          />
        </span>
            </Tooltip>

            {items.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    -
                </Typography>
            ) : (
                items.map((it) => (
                    <Tooltip key={it.key} title={it.title}>
            <span>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {React.cloneElement(it.icon, {
                    style: { color: metricColor(it.value) },
                })}
              </IconButton>
            </span>
                    </Tooltip>
                ))
            )}
        </Stack>
    );
}

export default function PalDevicesPage() {
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // debounce search
    const [debouncedFilter, setDebouncedFilter] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedFilter(globalFilter), 350);
        return () => clearTimeout(t);
    }, [globalFilter]);

    const { data, isLoading, isFetching, error, refetch } = useQuery({
        queryKey: ["palDevices", pagination.pageIndex, pagination.pageSize, debouncedFilter],
        queryFn: async () => {
            const limit = pagination.pageSize;
            const skip = pagination.pageIndex * pagination.pageSize;

            const url =
                `https://blue-parking-invite.bluept.dev/api/pal-devices` +
                `?limit=${limit}&skip=${skip}&filter=${encodeURIComponent(debouncedFilter || "")}`;

            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        },
        keepPreviousData: true,
    });

    const rows = data?.list ?? [];
    const rowCount = data?.count ?? 0;

    const cardBgColor = darkMode ? "#1e293b" : "#fff";

    // ----- Add Device modal -----
    const [openAdd, setOpenAdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        id: "",
        name: "",
        type: "",
        model: "",
        imei: "",
        iccid: "",
        phone_number: "",
        address_name: "",
        notes: "",
        health_json: "", // optional JSON text
    });

    const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    async function onAddDevice() {
        const id = (form.id || "").trim();
        if (!id) {
            alert("id is required");
            return;
        }

        const payload = {
            id,
            name: form.name?.trim() || null,
            type: form.type?.trim() || null,
            model: form.model?.trim() || null,
            imei: form.imei?.trim() || null,
            iccid: form.iccid?.trim() || null,
            phone_number: form.phone_number?.trim() || null,
            address_name: form.address_name?.trim() || null,
            notes: form.notes?.trim() || null,
        };

        // health (optional)
        const h = safeJsonParse(form.health_json?.trim());
        if (form.health_json?.trim() && !h) {
            alert("health_json is not valid JSON");
            return;
        }
        if (h) payload.health = h; // send as object

        try {
            setSaving(true);
            const res = await fetch("https://blue-parking-invite.bluept.dev/api/pal-devices", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const out = await res.json().catch(() => ({}));
            if (!res.ok || !out?.ok) {
                alert(out?.error || `Add failed (HTTP ${res.status})`);
                return;
            }

            // close + reset + refresh
            setOpenAdd(false);
            setForm({
                id: "",
                name: "",
                type: "",
                model: "",
                imei: "",
                iccid: "",
                phone_number: "",
                address_name: "",
                notes: "",
                health_json: "",
            });
            await refetch();
        } finally {
            setSaving(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: "ID",
                size: 220,
                Cell: ({ cell }) => (
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>{cell.getValue()}</span>
                ),
            },
            { accessorKey: "name", header: "Name", size: 220 },
            { accessorKey: "type", header: "Type", size: 120 },
            { accessorKey: "model", header: "Model", size: 120 },
            { accessorKey: "version", header: "Version", size: 120 },
            {
                accessorKey: "connected",
                header: "Connected",
                size: 120,
                Cell: ({ cell }) => {
                    const v = cell.getValue();
                    return (
                        <Chip
                            label={v ? "Online" : "Offline"}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                backgroundColor: v
                                    ? darkMode
                                        ? "rgba(34,197,94,0.18)"
                                        : "rgba(34,197,94,0.12)"
                                    : darkMode
                                        ? "rgba(239,68,68,0.18)"
                                        : "rgba(239,68,68,0.12)",
                                color: darkMode ? "#fff" : "#111",
                            }}
                        />
                    );
                },
            },

            // ✅ NEW: Health column (icons + tooltips)
            {
                id: "health",
                header: "Health",
                size: 170,
                accessorFn: (row) => row?.health,
                Cell: ({ row }) => (
                    <HealthIcons health={row.original.health} connected={!!row.original.connected} />
                ),
            },

            { accessorKey: "address_name", header: "Address", size: 260 },
            { accessorKey: "imei", header: "IMEI", size: 180 },
            { accessorKey: "iccid", header: "ICCID", size: 180 },
            { accessorKey: "phone_number", header: "Phone", size: 180 },
            { accessorKey: "created_at_iso", header: "Created", size: 170 },
        ],
        [darkMode]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <Box sx={{ p: 2 }}>
                <MaterialReactTable
                    columns={columns}
                    data={rows}
                    getRowId={(row) => row.id}
                    manualPagination
                    manualFiltering
                    rowCount={rowCount}
                    onPaginationChange={setPagination}
                    onGlobalFilterChange={setGlobalFilter}
                    muiTableHeadProps={{
                        sx: {
                            display: "table-header-group",
                            backgroundColor: "rgb(0,255,234)",
                            color: "white",
                        },
                    }}
                    state={{
                        pagination,
                        globalFilter,
                        isLoading,
                        showProgressBars: isFetching,
                    }}
                    enableRowActions
                    renderRowActions={({ row }) => (
                        <Button
                            color="error"
                            size="medium"
                            sx={{color: "red"}}
                            // variant="outlined"
                            onClick={async () => {
                                if (!window.confirm(`Delete device ${row.original.id}?`)) return;
                                const res = await fetch(`https://blue-parking-invite.bluept.dev/api/pal-devices/${row.original.id}`, {
                                    method: "DELETE",
                                });
                                const out = await res.json().catch(() => ({}));
                                if (!res.ok || !out?.ok) {
                                    alert(out?.error || "Delete failed");
                                    return;
                                }
                                await refetch();
                            }}
                        >
                           <Icon>delete</Icon>
                        </Button>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <Button variant="contained" onClick={() => setOpenAdd(true)}>
                            <Icon>add</Icon>
                        </Button>
                    )}
                    muiTableHeadCellProps={{
                        sx: {
                            backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                            color: "#fff",
                            fontWeight: "bold",
                            ".MuiSvgIcon-root": { color: darkMode ? "#ffffff" : "#000" },
                            "& .MuiTableSortLabel-icon": {
                                color: darkMode ? "#fff !important" : "#000 !important",
                            },
                            "& .MuiTableSortLabel-iconDirectionAsc": {
                                color: darkMode ? "#fff !important" : "#000 !important",
                            },
                            "& .MuiTableSortLabel-iconDirectionDesc": {
                                color: darkMode ? "#fff !important" : "#000 !important",
                            },
                            "& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root": {
                                color: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                            },
                            "& .MuiInputBase-input": {
                                color: darkMode ? "#fff" : "#1e293b",
                            },
                        },
                    }}
                    muiBottomToolbarProps={{
                        sx: {
                            backgroundColor: darkMode ? "#1e293b" : "#fff",
                            color: darkMode ? "#fff" : "#1e293b",
                            ".MuiFormLabel-root": { color: darkMode ? "#fff" : "#1e293b" },
                            ".MuiSelect-icon": { color: darkMode ? "#fff" : "#1e293b" },
                            ".MuiSelect-select": { color: darkMode ? "#fff" : "#1e293b" },
                            ".MuiBox-root.MuiButtonBase-root": { color: darkMode ? "#fff" : "#1e293b" },
                            ".MuiSvgIcon-root": { color: darkMode ? "#fff" : "#1e293b" },
                            "& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root": {
                                color: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                            },
                        },
                    }}
                    muiTableBodyCellProps={{
                        sx: {
                            backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                            color: darkMode ? "#ffffff" : "#000",
                            ".MuiSvgIcon-root": { color: darkMode ? "#ffffff" : "#000" },
                        },
                    }}
                    muiTableContainerProps={{
                        sx: { width: "100%", overflowX: "auto", backgroundColor: cardBgColor },
                    }}
                    muiTopToolbarProps={{
                        sx: {
                            backgroundColor: darkMode ? "#1e293b" : "#fff",
                            ".MuiSvgIcon-root": { color: darkMode ? "#fff" : "#1e293b" },
                            "& .MuiInputBase-input": { color: darkMode ? "#fff" : "#1e293b" },
                        },
                    }}
                    muiTableBodyRowProps={({ row }) => ({
                        sx: {
                            backgroundColor: darkMode
                                ? row.index % 2 === 0
                                    ? "rgba(255,255,255,0.03)"
                                    : "transparent"
                                : row.index % 2 === 0
                                    ? "rgba(0,0,0,0.02)"
                                    : "transparent",
                            "&:hover td": {
                                backgroundColor: darkMode ? "rgba(125, 211, 252, 0.10)" : "rgba(11, 99, 206, 0.08)",
                            },
                        },
                    })}
                />

                {error ? (
                    <Box sx={{ mt: 2, color: "tomato" }}>Error: {String(error?.message || error)}</Box>
                ) : null}
            </Box>

            {/* ✅ Add Device Dialog */}
            <Dialog   PaperProps={{
                sx: {
                    backgroundColor: "#fff",
                    color: "#000",
                },
            }} open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{color: "#000"}}>Add Device</DialogTitle>
                <DialogContent >
                    <Stack  sx={{
                        "& .MuiInputLabel-root": { color: "#000" },        // label
                        "& .MuiInputBase-input": { color: "#000" },       // typed text
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.25)" },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0,0,0,0.5)",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                        },
                        "& .MuiFormHelperText-root": { color: "rgba(0,0,0,0.7)" },
                    }} spacing={2}>
                        <TextField sx={{color: "#000"}} label="ID (required)" value={form.id} onChange={setF("id")} fullWidth />
                        <TextField sx={{color: "#000"}} label="Name" value={form.name} onChange={setF("name")} fullWidth />
                        <Stack sx={{color: "#000"}} direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField sx={{color: "#000"}} label="Type" value={form.type} onChange={setF("type")} fullWidth />
                            <TextField sx={{color: "#000"}} label="Model" value={form.model} onChange={setF("model")} fullWidth />
                        </Stack>

                        <TextField sx={{color: "#000"}} label="IMEI" value={form.imei} onChange={setF("imei")} fullWidth />
                        <TextField sx={{color: "#000"}} label="ICCID" value={form.iccid} onChange={setF("iccid")} fullWidth />
                        <TextField
                            label="Phone Number"
                            value={form.phone_number}
                            onChange={setF("phone_number")}
                            fullWidth
                            sx={{color: "#000"}}
                        />
                        <TextField
                            label="Address"
                            value={form.address_name}
                            onChange={setF("address_name")}
                            fullWidth
                            sx={{color: "#000"}}
                        />
                        <TextField
                            label="Notes"
                            value={form.notes}
                            onChange={setF("notes")}
                            fullWidth
                            multiline
                            sx={{color: "#000"}}
                            minRows={2}
                        />

                        <TextField
                            label="Health (optional JSON)"
                            placeholder='{"connectionStability":100,"cellularSignalNoise":50,"cellularSignalQuality":68}'
                            value={form.health_json}
                            onChange={setF("health_json")}
                            fullWidth
                            multiline
                            sx={{color: "#000"}}
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={onAddDevice}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}
